/**
 * Renders children when the user is authenticated.
 *
 * Order of trust:
 *  1. Native-auth session (the app's primary login) → render.
 *  2. An EXISTING MSAL/SSO account (useFetchWithMsal acquires API tokens silently) → render.
 *  3. Otherwise → send the user to the app's OWN /signin (native password flow).
 *
 * We deliberately do NOT drop into MsalAuthenticationTemplate to *trigger* a Microsoft redirect for an
 * unauthenticated user. That authenticated MSAL but never populated `native_auth_tokens` (what the app
 * actually uses), so after the SSO round-trip the app still saw no session and the user had to sign in a
 * SECOND time via the native form — the "enter username/password twice" bug. Password users now get one
 * clean native sign-in; genuine SSO users (an active MSAL account) are unaffected.
 */
import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useNativeAuth } from "context/NativeAuthContext";

function AuthGuard({ children }) {
  const { hasValidToken } = useNativeAuth();
  const { instance } = useMsal();
  const location = useLocation();

  if (hasValidToken) return children;
  // An already-established SSO session can render — API calls fall back to MSAL silent tokens.
  if (instance?.getActiveAccount?.()) return children;

  // Native-password user whose session lapsed → the app's own sign-in, preserving the intended page.
  return (
    <Navigate to="/signin" replace state={{ from: `${location.pathname}${location.search}` }} />
  );
}

AuthGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthGuard;
